import { useRef, useEffect, useState } from 'react';
import { createChart, CrosshairMode, ColorType, CandlestickSeries } from 'lightweight-charts';
import type { IChartApi, ISeriesApi, Time } from 'lightweight-charts';
import { ProviderFactory } from '../services/providers';
import type { StudyItem } from '../types/utils';
import type { ApiResponse, BaseCandleBar, Interval } from '@ocktra/shared-types';
import Dates from '../services/dates/dates';
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import { analyseClient } from '../services/api/apiClient';
import { useRealtimeCandles } from '../hooks/useRealtimeCandles';

const candleOptions = {
    upColor: '#26a69a',
    downColor: '#ef5350',
    borderVisible: false,
    wickUpColor: 'black',
    wickDownColor: 'black'
}

interface ChartComponentProps {
    instrument: string;
    timeframe: Interval;
    studies: StudyItem[];
    width: number;
    height: number;
    selected: boolean
}

const chartOptions = {
    layout: {
        textColor: 'black',
        background: {
            type: ColorType.Solid,
            color: 'white',
        },
    },
    crosshair: { mode: CrosshairMode.Normal },
    timeScale: {
        timeVisible: false,
        secondsVisible: false,
    },
    priceScale: {
        borderVisible: false,
    },
};

async function fetchData(id: string, _timeframe: Interval, last: number | undefined,  _dataFeedProvider: string) {
    try {
        const dates = new Dates().getToAndFromDates(_timeframe, last, "IN");
        console.log(dates, _timeframe);

        const provider = ProviderFactory.getDataFeedProvider("kite");
        if (!provider) {
            throw new Error("No data feed provider available");
        }

        const response = await provider.getHistoricalData(id, _timeframe, dates.from, dates.to);

        if (response) {
            console.log("Historical data response:", response);
            return {
                isEnd: response.length === 0,
                candles: response,
            };
        }
        return null;
    } catch (error) {
        console.error('Error fetching historical data:', error);
        return null;
    }
}

const ChartComponent: React.FC<ChartComponentProps> = ({ selected, instrument, timeframe, studies, width, height }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [noData, setNoData] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [candles, setCandles] = useState<BaseCandleBar[]>([]);
    const [studiesVisibility, setStudiesVisibility] = useState<Record<string, boolean>>({});
    const [analysisStatus, setAnalysisStatus] = useState<'idle' | 'analyzing' | 'success'>('idle');

    const navigate = useNavigate();
    const dataFeedProvider = "kite";

    const chartRef = useRef<HTMLDivElement | null>(null);
    const chartInstance = useRef<IChartApi | null>(null);
    const seriesInstance = useRef<ISeriesApi<'Candlestick'> | null>(null);
    const lastAvailableCandle = useRef<number | undefined>(undefined);
    const requestSent = useRef<boolean>(false);

    const analyzeCandles = async (candlesData: BaseCandleBar[]) => {
        setAnalysisStatus('analyzing');

        const stateResponse = await analyseClient.post<ApiResponse<string>>('/api/getState', {
            instrument,
            timeframe,
            userId: 'test-user'
        })

        if (!stateResponse.data) {
            const analyseResponse = await analyseClient.post<ApiResponse<string>>('/ai/analyse', {
                candles: candlesData,
                instrument,
                timeframe,
                userId: 'test-user'
            })
            console.log('Analysis Result:', analyseResponse.data);
        } else {
            console.log('Existing State:', stateResponse.data);
        }


        console.log('Analyzing candles:', {
            count: candles.length,
            instrument,
            timeframe,
            data: candles
        });

        setAnalysisStatus('success');
    };

    useEffect(() => {
        setLoading(true);
        setNoData(false);
        setError(null);


        lastAvailableCandle.current = undefined;

        fetchData(instrument, timeframe, lastAvailableCandle.current,  dataFeedProvider as any)
            .then((data) => {
                console.log("Fetched data for chart:", data);

                if (!data) {
                    setError('Failed to fetch data. Please check your connection.');
                    setLoading(false);
                    return;
                }

                const candlesData = data.candles;
                if (candlesData.length === 0) {
                    setNoData(true);
                    setLoading(false);
                    return;
                }

                if (chartRef.current) {
                    const chart = createChart(chartRef.current, {
                        ...chartOptions,
                        width: chartRef.current.clientWidth,
                        height: chartRef.current.clientHeight,
                        timeScale: {
                            ...chartOptions.timeScale,
                            timeVisible: true
                        },
                    });

                    seriesInstance.current = chart.addSeries(CandlestickSeries, candleOptions);
                    setCandles(candlesData);
                    lastAvailableCandle.current = candlesData[0].time as unknown as number;
                    setLoading(false);
                    chartInstance.current = chart;

                    analyzeCandles(candlesData)


                    chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
                        if (range != null && range.from < 5 && !requestSent.current) {
                            requestSent.current = true;
                            fetchData(instrument, timeframe, lastAvailableCandle.current,  dataFeedProvider as any)
                                .then((d) => {
                                    if (d && !d.isEnd) {
                                        setCandles((prev) => [...d.candles, ...prev]);
                                        lastAvailableCandle.current = d.candles[0]?.time as unknown as number || lastAvailableCandle.current;
                                        requestSent.current = false;
                                    }
                                })
                        }
                    })
                }
            })
            .catch((err) => {
                console.error("Error fetching chart data:", err);
                setError('Unable to connect to data provider.');
                setLoading(false);
            });

        return () => {
            chartInstance.current?.remove();
        };
    }, [instrument, timeframe, studies]);

    // Use custom hook for real-time candle updates with timeframe support
    const realtimeCandles = useRealtimeCandles({
        instrument,
        timeframe,
        initialCandles: candles
    });

    // Update chart when candles change
    useEffect(() => {
        if (seriesInstance.current && realtimeCandles.length > 0) {
            seriesInstance.current.setData(realtimeCandles.map(candle => ({
                ...candle,
                time: candle.time as unknown as Time,
            })));
        }
    }, [realtimeCandles]);

    useEffect(() => {
        if (chartInstance.current) {
            chartInstance.current.resize(parseInt(String(width)) - 6, parseInt(String(height)) - 6);
        }
    }, [width, height]);

    // Render overlay function for loading, error, and no-data states
    const renderOverlay = () => {
        if (loading) {
            return (
                <div className="absolute inset-0 bg-white flex items-center justify-center z-[9999]">
                    <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
                        <span className="text-gray-600 text-sm">Loading chart data...</span>
                    </div>
                </div>
            );
        }

        if (noData) {
            return (
                <div className="absolute inset-0 bg-white flex items-center justify-center z-[9999]">
                    <div className="flex flex-col items-center gap-3 text-center px-4">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="1.5">
                            <path d="M3 3v18h18" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M7 16l3-4 4 4 7-8" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="12" cy="12" r="1" fill="var(--color-text-tertiary)" />
                        </svg>
                        <div>
                            <h3 style={{
                                fontSize: 'var(--font-size-base)',
                                fontWeight: 'var(--font-weight-semibold)',
                                color: 'var(--color-text-primary)',
                                margin: '0 0 var(--spacing-xs) 0'
                            }}>
                                No Data Available
                            </h3>
                            <p style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)',
                                margin: 0
                            }}>
                                No chart data for {instrument}
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="absolute inset-0 bg-white flex items-center justify-center z-[9999]">
                    <div className="flex flex-col items-center gap-3 text-center px-4">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--color-danger-500)" strokeWidth="1.5">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 8v4" strokeLinecap="round" />
                            <circle cx="12" cy="16" r="0.5" fill="var(--color-danger-500)" />
                        </svg>
                        <div>
                            <h3 style={{
                                fontSize: 'var(--font-size-base)',
                                fontWeight: 'var(--font-weight-semibold)',
                                color: 'var(--color-text-primary)',
                                margin: '0 0 var(--spacing-xs) 0'
                            }}>
                                Connection Error
                            </h3>
                            <p style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)',
                                margin: '0 0 var(--spacing-md) 0'
                            }}>
                                {error}
                            </p>
                            <button
                                onClick={() => navigate("/account")}
                                style={{
                                    padding: 'var(--spacing-sm) var(--spacing-lg)',
                                    backgroundColor: 'var(--color-primary-600)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 'var(--radius-md)',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    cursor: 'pointer',
                                    transition: 'var(--transition-base)'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--color-primary-700)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--color-primary-600)';
                                }}
                            >
                                Configure Provider
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <div
            className={`flex align-center justify-center relative border-2 transition-colors ${selected ? 'border-gray-300' : 'border-white'}`}
            style={{ width, height }}
        >
            {/* Chart header with instrument info and study controls */}
            <div className="absolute top-2 left-4 z-[999] text-gray-500 font-semibold text-sm">
                <div className="mb-3">
                    <span>{instrument} - {timeframe}</span>
                </div>

                {/* Analysis indicator - below instrument name */}
                {!loading && !noData && !error && analysisStatus !== 'idle' && (
                    <div className="mb-3 flex items-center gap-2.5 bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                        <div
                            className={`flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 ${analysisStatus === 'analyzing' ? 'bg-blue-100' : 'bg-green-100'
                                }`}
                        >
                            {analysisStatus === 'analyzing' ? (
                                <div className="relative w-4 h-4">
                                    <div className="absolute inset-0 rounded-full border-2 border-blue-200"></div>
                                    <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-blue-600 animate-spin"></div>
                                </div>
                            ) : (
                                <svg
                                    className="w-4 h-4 text-green-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    strokeWidth={3}
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            )}
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <span
                                className={`font-semibold ${analysisStatus === 'analyzing' ? 'text-blue-700' : 'text-green-700'
                                    }`}
                                style={{ fontSize: '0.6875rem' }}
                            >
                                {analysisStatus === 'analyzing' ? 'Analysing...' : 'Analysed'}
                            </span>
                            <span
                                className={`font-medium ${analysisStatus === 'analyzing' ? 'text-blue-600' : 'text-green-600'
                                    }`}
                                style={{ fontSize: '0.5625rem' }}
                            >
                                {realtimeCandles.length} candles
                            </span>
                        </div>
                    </div>
                )}

                {studies.length > 0 && (
                    <div className="flex flex-col gap-1.5">
                        {studies.map(study => (
                            <div key={study.id} className="flex items-center gap-2">
                                <span className="text-xs">{study.name}</span>
                                <button
                                    onClick={() => setStudiesVisibility(prev => ({
                                        ...prev,
                                        [study.id]: !prev[study.id]
                                    }))}
                                    className="text-xs hover:text-gray-700 transition-colors"
                                    aria-label={`Toggle ${study.name} visibility`}
                                >
                                    {studiesVisibility[study.id] !== false ? (
                                        <FaEye className="w-3 h-3" />
                                    ) : (
                                        <FaEyeSlash className="w-3 h-3" />
                                    )}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* State-based overlays */}
            {renderOverlay()}

            {/* Chart container */}
            <div ref={chartRef} className="relative w-full h-full" />
        </div>
    );
};

export default ChartComponent

